<template>
  <ul class="user-list">
    <li
      v-for="user in users"
      :key="user.userId"
      class="user-list-item"
      :class="{ clickable: clickable }"
    >
      <slot name="userItem" :user="user">
        <template v-if="!clickable">
          <user-avatar
            :user-avatar-url="getUserAvatarUrl(user)"
            :user-id="user.userId"
            :is-game-avatar="isGameAvatar(user)"
          />
          <ellipsis-text
            class="name"
            :class="{ bold: isCurrentUser(user.userId) }"
            :text="getUserLabel(user)"
          />
        </template>
        <cv-link v-else type="button">
          <user-avatar
            :user-avatar-url="getUserAvatarUrl(user)"
            :user-id="user.userId"
            :is-game-avatar="isGameAvatar(user)"
          />
          <ellipsis-text
            class="name"
            :text="getUserLabel(user)"
            @click="handleUserClick(user.userId)"
          />
        </cv-link>
      </slot>
    </li>
  </ul>
</template>

<script setup lang="ts">
import { useStore } from "vuex";
import { NAMESPACE } from "@modules/attempt/service/attempt.constant";
import UserAvatarMixin from "@modules/user-avatar/mixin/user.avatar.mixin";
import GamificationAvatarMixin from "@modules/attempt/mixin/gamification.avatar.mixin";
import { computed } from "vue";

// Mixin
defineOptions({
  mixins: [UserAvatarMixin, GamificationAvatarMixin],
});

// Props
interface Props {
  users: unknown[];
  clickable?: boolean;
}

const { users = [], clickable = false } = defineProps<Props>();

// Emits
const emit = defineEmits<{
  userClicked: [data: unknown];
}>();

// Composables
const store = useStore();

// Computed
const currentUserId = computed(() => store.getters[`${NAMESPACE.ATTEMPT}/currentUserId`]);

// Methods
const handleUserClick = (userId) => {
  if (clickable) {
    emit("userClicked", userId);
  }
};

const getUserLabel = (user) => {
  if (user.classSerialNo && user.classSerialNo != -1) {
    return `${user.classSerialNo}. ${user.name}`;
  } else {
    return user.name;
  }
};

const isCurrentUser = (userId) => {
  return currentUserId.value === userId.toString();
};
</script>

<style lang="scss">
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.user-list {
  display: flex;
  flex-flow: row wrap;
  color: $text-01;
  @include carbon--type-style("body-short-01", true);

  .user-list-item {
    display: flex;
    align-items: center;
    flex: 0 0 100%;
    max-width: 100%;
    padding: $divider-space-01 0;
    border-bottom: $divider-02;

    &:first-child {
      border-top: $divider-02;
    }

    &:last-child {
      border-bottom: none;
    }

    .bx--link {
      display: flex;
      align-items: center;
      width: 100%;
      color: $text-01;
      @include carbon--type-style("body-short-01", true);

      &:hover {
        text-decoration: none;
      }
    }

    span {
      &.bold {
        @include carbon--type-style("productive-heading-02");
      }
    }
  }

  .user-avatar {
    margin-right: $spacing-05;
  }
}
</style>
